/**
 * useSideView: the floating windows of Karr, open over the page being worked.
 *
 * Founder, 26 Sep 2026, second pass: "when we click the button it duplicates
 * the current screen but takes only 50% screen which can then be resized but
 * comes float on top and then on that screen we can do what we do normally ...
 * this can be repeated uptill any times if there is no problem else limit the
 * number of screens".
 *
 * So each window is a second Karr (see frame.ts), with its own sidebar, opened
 * on the page the main window is showing. Every window is a whole app running
 * in the browser, which is why there is a ceiling: MAX_WINDOWS.
 *
 * Geometry is in CSS px of the viewport. The windows and their geometry are
 * remembered for this browser tab (sessionStorage): a reload brings them back,
 * tomorrow's visit starts clean. Nothing here reads or writes the database.
 *
 * @since 26 Sep 2026 - sideview (windows: sidewin)
 */
import { create } from 'zustand';

export interface Rect { x: number; y: number; w: number; h: number }

export interface SideWindow extends Rect {
    id: string;
    /** Path + query inside Karr, e.g. `/reports?tab=profit-loss`. */
    href: string;
    /** What the title bar says: the page's own title once it has loaded. */
    title: string;
    /** Stacking order; the highest is in front. */
    z: number;
    /** Filling the work area; `x/y/w/h` is where it goes back to. */
    maximized: boolean;
    /**
     * Put away to a chip in the app header (winmin). The window stays LOADED,
     * so restoring it brings back the page exactly as it was left.
     */
    minimized: boolean;
}

/** Each window is a whole Karr in the browser. Past four a laptop starts to feel it. */
export const MAX_WINDOWS = 4;
/** The smallest a window may be dragged to: a phone's layout still fits. */
export const MIN_W = 360;
export const MIN_H = 260;
/** How far each new window steps from the one before, so none hides another. */
const CASCADE = 32;

interface State {
    windows: SideWindow[];
    /** Open a window on `href`. False when MAX_WINDOWS are already open. */
    open: (href: string, title: string, area: Rect) => boolean;
    close: (id: string) => void;
    closeAll: () => void;
    focus: (id: string) => void;
    setRect: (id: string, r: Rect) => void;
    toggleMax: (id: string) => void;
    minimize: (id: string) => void;
    /** Out of the header and back on top. */
    restore: (id: string) => void;
    /** The page inside the window moved (a link, the sidebar): follow it. */
    follow: (id: string, href: string, title: string | null) => void;
}

const KEY = 'karr.sidewin.v1';

function load(): SideWindow[] {
    try {
        const raw = window.sessionStorage.getItem(KEY);
        if (!raw) return [];
        const v = JSON.parse(raw) as unknown;
        if (!Array.isArray(v)) return [];
        return v.filter((w): w is SideWindow => !!w && typeof w === 'object'
            && typeof (w as SideWindow).id === 'string'
            && typeof (w as SideWindow).href === 'string' && (w as SideWindow).href.startsWith('/')
            && typeof (w as SideWindow).title === 'string'
            && ['x', 'y', 'w', 'h', 'z'].every(k => Number.isFinite((w as unknown as Record<string, number>)[k])))
            .map(w => ({ ...w, maximized: !!w.maximized, minimized: !!w.minimized }))
            .slice(0, MAX_WINDOWS);
    } catch {
        // Private windows and blocked site data throw on the read itself.
        return [];
    }
}

/**
 * The work area windows live in: everything under the app header (and the
 * ticker under it, when one is showing), with a small margin. Windows never
 * cover the header, so the button that opens the next one is always in reach.
 */
export function workArea(): Rect {
    const main = typeof document !== 'undefined' ? document.getElementById('main-content') : null;
    const top = Math.round(main?.getBoundingClientRect().top ?? 48);
    const m = 8;
    return { x: m, y: top + m, w: window.innerWidth - m * 2, h: window.innerHeight - top - m * 2 };
}

let seq = 0;
const newId = () => `w${Date.now().toString(36)}${(seq++).toString(36)}`;
const topZ = (ws: SideWindow[]) => ws.reduce((m, w) => Math.max(m, w.z), 0);

/**
 * Where a new window goes: the right half of the work area, stepped left and
 * down from the windows already open so each one's title bar stays in sight.
 */
export function placeNew(area: Rect, count: number): Rect {
    const w = Math.max(MIN_W, Math.round(area.w * 0.5));
    const step = CASCADE * count;
    const x = Math.max(area.x, area.x + area.w - w - step);
    const y = Math.min(area.y + step, area.y + Math.max(0, area.h - MIN_H));
    return { x, y, w, h: Math.max(MIN_H, area.y + area.h - y) };
}

export const useSideView = create<State>((set, get) => ({
    windows: typeof window === 'undefined' ? [] : load(),

    open: (href, title, area) => {
        const ws = get().windows;
        if (ws.length >= MAX_WINDOWS) return false;
        const win: SideWindow = { id: newId(), href, title, z: topZ(ws) + 1, maximized: false, minimized: false, ...placeNew(area, ws.length) };
        set({ windows: [...ws, win] });
        return true;
    },

    close: (id) => set(s => ({ windows: s.windows.filter(w => w.id !== id) })),
    closeAll: () => set({ windows: [] }),

    focus: (id) => set(s => {
        const w = s.windows.find(x => x.id === id);
        const top = topZ(s.windows);
        if (!w || w.z === top) return {};
        return { windows: s.windows.map(x => (x.id === id ? { ...x, z: top + 1 } : x)) };
    }),

    setRect: (id, r) => set(s => ({ windows: s.windows.map(w => (w.id === id ? { ...w, ...r, maximized: false } : w)) })),

    toggleMax: (id) => set(s => ({ windows: s.windows.map(w => (w.id === id ? { ...w, maximized: !w.maximized } : w)) })),

    minimize: (id) => set(s => ({ windows: s.windows.map(w => (w.id === id ? { ...w, minimized: true } : w)) })),

    restore: (id) => set(s => {
        const top = topZ(s.windows);
        return { windows: s.windows.map(w => (w.id === id ? { ...w, minimized: false, z: top + 1 } : w)) };
    }),

    follow: (id, href, title) => set(s => ({
        windows: s.windows.map(w => (w.id === id ? { ...w, href, title: title || w.title } : w)),
    })),
}));

// Remember the windows for this browser tab, whenever they change.
if (typeof window !== 'undefined') {
    useSideView.subscribe((s, prev) => {
        if (s.windows === prev.windows) return;
        try { window.sessionStorage.setItem(KEY, JSON.stringify(s.windows)); } catch { /* still works, just not remembered */ }
    });
}
